/*
  Gaia Iluka — The Letter: the edge tab's panel
  File: assets/gi-letter.js

  Almost everything here is delegated to <dialog>. showModal() supplies
  the Escape key, the focus trap, the backdrop and the top layer, and
  inerts the page behind — four things a hand-rolled panel gets subtly
  wrong. What is left is small enough to read in one sitting:

    - open on the tab
    - close on the close button
    - close on a click outside the panel
    - reopen the panel if the customer form came back with an error or a
      success, so the answer is not lost behind a closed door

  OUTSIDE CLICK, MEASURED NOT GUESSED. A click on a modal dialog's
  backdrop has the dialog as its target, because the backdrop is painted
  by the dialog itself and has no node of its own. So the test is
  geometric: was the pointer outside the dialog's box. Comparing
  event.target against the panel would close it on every click inside
  the padding.

  NO SCROLL LOCK OF OUR OWN. showModal() already prevents the page
  behind from scrolling. Adding overflow:hidden on <body> on top of that
  is what causes the scroll position to jump on iOS.
*/
(function () {
  'use strict';

  if (window.__giLetterLoaded) return;
  window.__giLetterLoaded = true;

  function init(root) {
    var panel = root.querySelector('[id="giLetterPanel"]') || root.querySelector('dialog');
    var openBtn = root.querySelector('[data-gi-letter-open]');
    if (!panel || !openBtn || typeof panel.showModal !== 'function') return;
    if (panel.dataset.giLetterReady === '1') return;
    panel.dataset.giLetterReady = '1';

    function open() {
      if (!panel.open) panel.showModal();
    }

    function close() {
      if (panel.open) panel.close();
    }

    /* Keep the phone dialog inside Safari's actually visible viewport.
       The visual viewport shrinks when its software keyboard appears. */
    var visualViewport = window.visualViewport;
    function syncVisualViewport() {
      var height = visualViewport ? visualViewport.height : window.innerHeight;
      var top = visualViewport ? visualViewport.offsetTop : 0;
      root.style.setProperty('--gi-letter-visual-height', height + 'px');
      root.style.setProperty('--gi-letter-visual-center', (top + height / 2) + 'px');
      var obscuredBottom = visualViewport
        ? Math.max(0, window.innerHeight - (visualViewport.offsetTop + visualViewport.height))
        : 0;
      root.style.setProperty('--gi-letter-browser-inset', obscuredBottom + 'px');
    }
    syncVisualViewport();
    if (visualViewport) {
      visualViewport.addEventListener('resize', syncVisualViewport, { passive: true });
      visualViewport.addEventListener('scroll', syncVisualViewport, { passive: true });
    }
    window.addEventListener('resize', syncVisualViewport, { passive: true });

    /* Some touch browsers keep :focus-visible on a button after a tap.
       Track input modality so the tab gets its designed keyboard ring
       without leaving a frame after pointer interaction. */
    function markPointerInput() {
      root.setAttribute('data-gi-letter-pointer-input', '');
    }
    function markKeyboardInput() {
      root.removeAttribute('data-gi-letter-pointer-input');
    }
    document.addEventListener('pointerdown', markPointerInput, true);
    document.addEventListener('keydown', markKeyboardInput, true);

    /* Mirror the native navigation drawer's actual open state onto the
       Letter component. This keeps the fixed tab out of the drawer even
       on mobile browsers where relational CSS selectors vary. */
    var menuDetails = document.getElementById('Details-menu-drawer-container');
    if (menuDetails) {
      function syncMenuState() {
        var menuOpen = menuDetails.hasAttribute('open') || menuDetails.classList.contains('menu-open');
        root.toggleAttribute('data-gi-letter-menu-open', menuOpen);
        document.body.toggleAttribute('data-gi-letter-menu-open', menuOpen);
        if (menuOpen) close();
      }

      syncMenuState();
      var menuStateObserver = new MutationObserver(syncMenuState);
      menuStateObserver.observe(menuDetails, { attributes: true, attributeFilter: ['open', 'class'] });
      document.addEventListener('shopify:section:unload', function (evt) {
        if (!evt.target.contains(root)) return;
        menuStateObserver.disconnect();
        document.removeEventListener('pointerdown', markPointerInput, true);
        document.removeEventListener('keydown', markKeyboardInput, true);
        window.removeEventListener('resize', syncVisualViewport);
        if (visualViewport) {
          visualViewport.removeEventListener('resize', syncVisualViewport);
          visualViewport.removeEventListener('scroll', syncVisualViewport);
        }
      });
    }

    openBtn.addEventListener('click', open);

    root.querySelectorAll('[data-gi-letter-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });

    panel.addEventListener('click', function (evt) {
      /* Only the dialog itself can be the backdrop's target; anything
         inside bubbles from a child. Even so, check the geometry — a
         click on the dialog's own padding also targets the dialog. */
      if (evt.target !== panel) return;
      var r = panel.getBoundingClientRect();
      var outside =
        evt.clientX < r.left ||
        evt.clientX > r.right ||
        evt.clientY < r.top ||
        evt.clientY > r.bottom;
      if (outside) close();
    });

    /* Focus belongs on the panel's first control, not wherever the
       browser lands. Deferred a frame so the dialog is laid out. */
    panel.addEventListener('close', function () {
      try { openBtn.focus({ preventScroll: true }); } catch (e) { openBtn.focus(); }
    });

    /* The customer form posts and the page reloads. If it came back with
       something to say, the panel has to be showing to say it. */
    var answered = panel.querySelector('.gi-letter-form__success, .gi-letter-form__error');
    if (answered) {
      requestAnimationFrame(open);
    }

    /* On phones the homepage hero owns the lower viewport. Keep the Letter
       available everywhere else, but return the tab after the visitor has
       moved beyond that hero. */
    var hero = document.querySelector('.gi-home-hero-section .gi-hero');
    var compactViewport = window.matchMedia('(max-width: 749px)');
    var tabWasHidden = compactViewport.matches && root.hasAttribute('data-gi-letter-hero-hidden');
    var lastScrollY = window.scrollY;
    var scrollFrame = 0;

    if (!compactViewport.matches && root.hasAttribute('data-gi-letter-hero-hidden')) {
      root.removeAttribute('data-gi-letter-hero-hidden');
    }

    if (tabWasHidden) {
      openBtn.tabIndex = -1;
      openBtn.setAttribute('aria-hidden', 'true');
    }

    function setHeroTabState(hidden) {
      if (hidden === tabWasHidden) return;
      tabWasHidden = hidden;
      root.toggleAttribute('data-gi-letter-hero-hidden', hidden);
      openBtn.tabIndex = hidden ? -1 : 0;
      openBtn.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    }

    if (hero && 'IntersectionObserver' in window) {
      var heroObserver = new IntersectionObserver(function (entries) {
        setHeroTabState(compactViewport.matches && entries[0].isIntersecting);
      }, { threshold: 0.01 });

      heroObserver.observe(hero);
      compactViewport.addEventListener('change', function () {
        if (!compactViewport.matches) {
          setHeroTabState(false);
        } else {
          var bounds = hero.getBoundingClientRect();
          setHeroTabState(bounds.bottom > 0 && bounds.top < window.innerHeight);
        }
      });
    } else if (hero) {
      function syncHeroFallback() {
        var bounds = hero.getBoundingClientRect();
        setHeroTabState(compactViewport.matches && bounds.bottom > 0 && bounds.top < window.innerHeight);
      }
      syncHeroFallback();
      window.addEventListener('scroll', syncHeroFallback, { passive: true });
      window.addEventListener('resize', syncHeroFallback, { passive: true });
    }

    /* Every page ends with the same form in the closing section (and The
       Table's homepage carries its own). While that form is on screen the
       tab would be offering a second copy of it a few pixels away, so it
       steps aside and returns as soon as the visitor scrolls back up. */
    var inlineForm = document.querySelector('[data-gi-letter-inline]');
    if (inlineForm && 'IntersectionObserver' in window) {
      var inlineObserver = new IntersectionObserver(function (entries) {
        var visible = entries[0].isIntersecting;
        root.toggleAttribute('data-gi-letter-inline-visible', visible);
        if (!tabWasHidden) {
          openBtn.tabIndex = visible ? -1 : 0;
          openBtn.setAttribute('aria-hidden', visible ? 'true' : 'false');
        }
      }, { threshold: 0.2 });
      inlineObserver.observe(inlineForm);
    }

    /* Safari expands its bottom controls as the visitor scrolls back up.
       Keep the invitation clear of the portion of the layout viewport
       hidden by browser chrome, and let it settle back to the edge while
       the visitor scrolls down. */
    function trackMobileScroll() {
      if (!compactViewport.matches || panel.open) return;
      var currentY = window.scrollY;
      var delta = currentY - lastScrollY;
      if (Math.abs(delta) >= 4) {
        root.setAttribute('data-gi-letter-scroll-direction', delta > 0 ? 'down' : 'up');
      }
      lastScrollY = currentY;
    }
    window.addEventListener('scroll', function () {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(function () {
        scrollFrame = 0;
        trackMobileScroll();
      });
    }, { passive: true });
    compactViewport.addEventListener('change', function () {
      lastScrollY = window.scrollY;
      if (!compactViewport.matches) root.removeAttribute('data-gi-letter-scroll-direction');
    });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-gi-letter]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Theme editor: the section can be re-rendered under us. */
  document.addEventListener('shopify:section:load', function (evt) { boot(evt.target); });
})();
